/**
 * Fantasy Console 0 - Map Canvas Component
 *
 * Main canvas for displaying and editing the map.
 * Optimized for performance with sprite sheet caching and RAF-based rendering.
 */

import { useRef, useEffect, useCallback } from 'react'
import {
  MAP_WIDTH,
  MAP_HEIGHT,
  SPRITE_SIZE,
  SPRITE_SHEET_SIZE,
  SPRITES_PER_ROW,
} from '@fc0/types'
import { PICO8_RGB } from '../../utils/spriteUtils'
import {
  screenToTile,
  isValidTilePosition,
  getVisibleTileRange,
  setMapTileMut,
  getMapTile,
  floodFillMap,
  fillRectMap,
} from '../../utils/mapUtils'
import styles from './MapCanvas.module.css'

export type MapTool = 'pencil' | 'fill' | 'rect' | 'eraser' | 'picker'

interface MapCanvasProps {
  mapData: Uint8Array
  spriteData: Uint8Array
  selectedTile: number
  activeTool: MapTool
  zoom: number
  cameraX: number
  cameraY: number
  showGrid?: boolean
  onMapChange: (newMap: Uint8Array) => void
  onCameraChange: (x: number, y: number) => void
  onTilePick?: (tileIndex: number) => void
  onCursorChange?: (x: number, y: number) => void
}

export function MapCanvas({
  mapData,
  spriteData,
  selectedTile,
  activeTool,
  zoom,
  cameraX,
  cameraY,
  showGrid = true,
  onMapChange,
  onCameraChange,
  onTilePick,
  onCursorChange,
}: MapCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  // Drawing state refs (no re-renders)
  const isDrawingRef = useRef(false)
  const isPanningRef = useRef(false)
  const lastPanPosRef = useRef({ x: 0, y: 0 })
  const localMapRef = useRef<Uint8Array | null>(null)
  const rectStartRef = useRef<{ x: number; y: number } | null>(null)
  const cursorTileRef = useRef<{ x: number; y: number } | null>(null)

  // Cached sprite sheet canvas (pre-rendered at 1x scale)
  const spriteSheetCanvasRef = useRef<HTMLCanvasElement | null>(null)

  // Props refs for event handlers (avoid stale closures)
  const propsRef = useRef({
    mapData,
    spriteData,
    selectedTile,
    activeTool,
    zoom,
    cameraX,
    cameraY,
    showGrid,
  })
  propsRef.current = {
    mapData,
    spriteData,
    selectedTile,
    activeTool,
    zoom,
    cameraX,
    cameraY,
    showGrid,
  }

  // Pre-render sprite sheet to canvas (only when sprites change)
  useEffect(() => {
    const canvas = document.createElement('canvas')
    canvas.width = SPRITE_SHEET_SIZE
    canvas.height = SPRITE_SHEET_SIZE
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const imageData = ctx.createImageData(SPRITE_SHEET_SIZE, SPRITE_SHEET_SIZE)
    for (let i = 0; i < spriteData.length; i++) {
      const colorIndex = spriteData[i]
      const [r, g, b] = PICO8_RGB[colorIndex]
      const idx = i * 4
      imageData.data[idx] = r
      imageData.data[idx + 1] = g
      imageData.data[idx + 2] = b
      imageData.data[idx + 3] = 255
    }
    ctx.putImageData(imageData, 0, 0)
    spriteSheetCanvasRef.current = canvas
  }, [spriteData])

  // Render function (called via RAF or effect)
  const render = useCallback(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    const spriteSheet = spriteSheetCanvasRef.current
    if (!canvas || !container || !spriteSheet) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return

    const {
      mapData: map,
      zoom: z,
      cameraX: camX,
      cameraY: camY,
      selectedTile: tile,
      activeTool: tool,
      showGrid: grid,
    } = propsRef.current
    const mapToRender = localMapRef.current || map

    const containerWidth = container.clientWidth
    const containerHeight = container.clientHeight

    // Only resize if needed
    if (canvas.width !== containerWidth || canvas.height !== containerHeight) {
      canvas.width = containerWidth
      canvas.height = containerHeight
    }

    const tileSize = SPRITE_SIZE * z

    // Calculate visible range
    const { startX, startY, endX, endY } = getVisibleTileRange(
      camX,
      camY,
      containerWidth,
      containerHeight,
      z,
    )

    // Clear canvas
    ctx.fillStyle = '#000000'
    ctx.fillRect(0, 0, containerWidth, containerHeight)

    ctx.imageSmoothingEnabled = false

    // Render visible tiles directly from sprite sheet
    for (let ty = startY; ty < endY; ty++) {
      for (let tx = startX; tx < endX; tx++) {
        const tileIndex = mapToRender[ty * MAP_WIDTH + tx]
        const srcX = (tileIndex % SPRITES_PER_ROW) * SPRITE_SIZE
        const srcY = Math.floor(tileIndex / SPRITES_PER_ROW) * SPRITE_SIZE
        const destX = (tx - camX) * tileSize
        const destY = (ty - camY) * tileSize

        ctx.drawImage(
          spriteSheet,
          srcX,
          srcY,
          SPRITE_SIZE,
          SPRITE_SIZE,
          destX,
          destY,
          tileSize,
          tileSize,
        )
      }
    }

    // Draw grid
    if (grid) {
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)'
      ctx.lineWidth = 1
      const gridStartX = 0
      const gridEndX = MAP_WIDTH
      const gridStartY = 0
      const gridEndY = MAP_HEIGHT

      // draw 4 tile guidelines
      for (let tx = gridStartX; tx <= gridEndX; tx += 4) {
        const screenX = Math.floor((tx - camX) * tileSize) + 0.5
        ctx.beginPath()
        ctx.moveTo(screenX, -camY * tileSize)
        ctx.lineTo(screenX, (MAP_HEIGHT - camY) * tileSize)
        ctx.stroke()
      }
      for (let ty = gridStartY; ty <= gridEndY; ty += 4) {
        const screenY = Math.floor((ty - camY) * tileSize) + 0.5
        ctx.beginPath()
        ctx.moveTo(-camX * tileSize, screenY)
        ctx.lineTo((MAP_WIDTH - camX) * tileSize, screenY)
        ctx.stroke()
      }

      // draw screen size guidelines
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.5)'
      for (let tx = gridStartX; tx <= gridEndX; tx += 16) {
        if (tx === 0) continue
        const screenX = Math.floor((tx - camX) * tileSize) + 0.5
        ctx.beginPath()
        ctx.moveTo(screenX, -camY * tileSize)
        ctx.lineTo(screenX, (MAP_HEIGHT - camY) * tileSize)
        ctx.stroke()
      }
      for (let ty = gridStartY; ty <= gridEndY; ty += 16) {
        if (ty === 0) continue
        const screenY = Math.floor((ty - camY) * tileSize) + 0.5
        ctx.beginPath()
        ctx.moveTo(-camX * tileSize, screenY)
        ctx.lineTo((MAP_WIDTH - camX) * tileSize, screenY)
        ctx.stroke()
      }
    }

    // Draw cursor preview
    const cursor = cursorTileRef.current
    if (cursor && isValidTilePosition(cursor.x, cursor.y)) {
      const screenX = (cursor.x - camX) * tileSize
      const screenY = (cursor.y - camY) * tileSize

      // Highlight current tile
      ctx.strokeStyle = '#29adff'
      ctx.lineWidth = 2
      ctx.strokeRect(screenX + 1, screenY + 1, tileSize - 2, tileSize - 2)

      // Show selected tile preview (semi-transparent)
      if (tool !== 'picker' && tool !== 'eraser') {
        ctx.globalAlpha = 0.5
        const srcX = (tile % SPRITES_PER_ROW) * SPRITE_SIZE
        const srcY = Math.floor(tile / SPRITES_PER_ROW) * SPRITE_SIZE
        ctx.drawImage(
          spriteSheet,
          srcX,
          srcY,
          SPRITE_SIZE,
          SPRITE_SIZE,
          screenX,
          screenY,
          tileSize,
          tileSize,
        )
        ctx.globalAlpha = 1
      }
    }

    // Draw rectangle preview
    const rectStart = rectStartRef.current
    if (rectStart && cursor && tool === 'rect') {
      const minX = Math.min(rectStart.x, cursor.x)
      const maxX = Math.max(rectStart.x, cursor.x)
      const minY = Math.min(rectStart.y, cursor.y)
      const maxY = Math.max(rectStart.y, cursor.y)

      const rectScreenX = (minX - camX) * tileSize
      const rectScreenY = (minY - camY) * tileSize
      const rectWidth = (maxX - minX + 1) * tileSize
      const rectHeight = (maxY - minY + 1) * tileSize

      ctx.strokeStyle = '#ffec27'
      ctx.lineWidth = 2
      ctx.setLineDash([4, 4])
      ctx.strokeRect(rectScreenX, rectScreenY, rectWidth, rectHeight)
      ctx.setLineDash([])
    }
  }, [])

  // Initial render and re-render on prop changes
  useEffect(() => {
    render()
  }, [
    mapData,
    spriteData,
    zoom,
    cameraX,
    cameraY,
    selectedTile,
    activeTool,
    showGrid,
    render,
  ])

  // Get tile position from mouse event
  const getTileFromEvent = useCallback(
    (e: React.MouseEvent | MouseEvent): { x: number; y: number } | null => {
      const canvas = canvasRef.current
      if (!canvas) return null

      const rect = canvas.getBoundingClientRect()
      const { cameraX: camX, cameraY: camY, zoom: z } = propsRef.current
      return screenToTile(e.clientX, e.clientY, rect, camX, camY, z)
    },
    [],
  )

  // Place a tile at position
  const placeTile = useCallback(
    (x: number, y: number, tile: number) => {
      if (!isValidTilePosition(x, y)) return

      if (!localMapRef.current) {
        localMapRef.current = new Uint8Array(propsRef.current.mapData)
      }
      setMapTileMut(localMapRef.current, x, y, tile)
      render()
    },
    [render],
  )

  // Commit local changes
  const commitChanges = useCallback(() => {
    if (localMapRef.current) {
      onMapChange(localMapRef.current)
      localMapRef.current = null
    }
  }, [onMapChange])

  // Handle mouse down
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()

      // Middle mouse or alt+click for panning
      if (e.button === 1 || (e.button === 0 && e.altKey)) {
        isPanningRef.current = true
        lastPanPosRef.current = { x: e.clientX, y: e.clientY }
        return
      }

      // Left click for drawing
      if (e.button === 0) {
        const tilePos = getTileFromEvent(e)
        if (!tilePos || !isValidTilePosition(tilePos.x, tilePos.y)) return

        const {
          activeTool: tool,
          selectedTile: tile,
          mapData: map,
        } = propsRef.current

        switch (tool) {
          case 'pencil':
            isDrawingRef.current = true
            placeTile(tilePos.x, tilePos.y, tile)
            break

          case 'eraser':
            isDrawingRef.current = true
            placeTile(tilePos.x, tilePos.y, 0)
            break

          case 'fill': {
            const newMap = floodFillMap(map, tilePos.x, tilePos.y, tile)
            onMapChange(newMap)
            break
          }

          case 'rect':
            isDrawingRef.current = true
            rectStartRef.current = tilePos
            break

          case 'picker': {
            const pickedTile = getMapTile(map, tilePos.x, tilePos.y)
            onTilePick?.(pickedTile)
            break
          }
        }
      }
    },
    [getTileFromEvent, placeTile, onMapChange, onTilePick],
  )

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      const tilePos = getTileFromEvent(e)

      // Update cursor position (ref, no re-render)
      if (tilePos && isValidTilePosition(tilePos.x, tilePos.y)) {
        cursorTileRef.current = tilePos
        onCursorChange?.(tilePos.x, tilePos.y)
      } else {
        cursorTileRef.current = null
      }

      // Handle panning
      if (isPanningRef.current) {
        const { zoom: z, cameraX: camX, cameraY: camY } = propsRef.current
        const dx = e.clientX - lastPanPosRef.current.x
        const dy = e.clientY - lastPanPosRef.current.y
        const tileSize = SPRITE_SIZE * z

        // Round to integers to keep tile alignment
        const newCameraX = Math.round(
          Math.max(0, Math.min(MAP_WIDTH - 1, camX - dx / tileSize)),
        )
        const newCameraY = Math.round(
          Math.max(0, Math.min(MAP_HEIGHT - 1, camY - dy / tileSize)),
        )

        // Clear cursor during scroll to prevent misalignment
        cursorTileRef.current = null

        onCameraChange(newCameraX, newCameraY)
        lastPanPosRef.current = { x: e.clientX, y: e.clientY }
        return
      }

      // Handle drawing
      if (
        isDrawingRef.current &&
        tilePos &&
        isValidTilePosition(tilePos.x, tilePos.y)
      ) {
        const { activeTool: tool, selectedTile: tile } = propsRef.current

        switch (tool) {
          case 'pencil':
            placeTile(tilePos.x, tilePos.y, tile)
            break

          case 'eraser':
            placeTile(tilePos.x, tilePos.y, 0)
            break

          case 'rect':
            // Just re-render for preview
            render()
            break
        }
      } else {
        // Just update cursor preview
        render()
      }
    },
    [getTileFromEvent, placeTile, onCameraChange, onCursorChange, render],
  )

  // Handle mouse up
  const handleMouseUp = useCallback(
    (e: React.MouseEvent) => {
      if (isPanningRef.current) {
        isPanningRef.current = false
        return
      }

      if (isDrawingRef.current) {
        const {
          activeTool: tool,
          selectedTile: tile,
          mapData: map,
        } = propsRef.current
        const cursor = cursorTileRef.current

        // Handle rectangle tool completion
        if (tool === 'rect' && rectStartRef.current && cursor) {
          const { x: x0, y: y0 } = rectStartRef.current
          const { x: x1, y: y1 } = cursor
          const filled = !e.shiftKey
          const newMap = fillRectMap(map, x0, y0, x1, y1, tile, filled)
          onMapChange(newMap)
          localMapRef.current = null
        } else {
          commitChanges()
        }

        isDrawingRef.current = false
        rectStartRef.current = null
      }
    },
    [onMapChange, commitChanges],
  )

  // Handle mouse leave
  const handleMouseLeave = useCallback(() => {
    cursorTileRef.current = null
    render()

    if (isDrawingRef.current && propsRef.current.activeTool !== 'rect') {
      commitChanges()
      isDrawingRef.current = false
    }
  }, [commitChanges, render])

  // Handle wheel for pan (native event listener to allow preventDefault)
  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault()

      const { zoom: z, cameraX: camX, cameraY: camY } = propsRef.current
      const tileSize = SPRITE_SIZE * (z / 6)
      const dx = e.deltaX / tileSize
      const dy = e.deltaY / tileSize

      // Round to integers to keep tile alignment
      const newCameraX = Math.round(
        Math.max(0, Math.min(MAP_WIDTH - 1, camX + dx)),
      )
      const newCameraY = Math.round(
        Math.max(0, Math.min(MAP_HEIGHT - 1, camY + dy)),
      )

      // Clear cursor during scroll to prevent misalignment
      cursorTileRef.current = null

      onCameraChange(newCameraX, newCameraY)
    }

    canvas.addEventListener('wheel', handleWheel, { passive: false })
    return () => canvas.removeEventListener('wheel', handleWheel)
  }, [onCameraChange])

  // Handle context menu (right-click to pick)
  const handleContextMenu = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()

      const tilePos = getTileFromEvent(e)
      if (tilePos && isValidTilePosition(tilePos.x, tilePos.y)) {
        const pickedTile = getMapTile(
          propsRef.current.mapData,
          tilePos.x,
          tilePos.y,
        )
        onTilePick?.(pickedTile)
      }
    },
    [getTileFromEvent, onTilePick],
  )

  return (
    <div className={styles.container} ref={containerRef}>
      <canvas
        ref={canvasRef}
        className={styles.canvas}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        onContextMenu={handleContextMenu}
        tabIndex={0}
        role='grid'
        aria-label='Map editor canvas'
      />
    </div>
  )
}
